// --- Types ---

export interface Filters {
  price: [number, number];
  adv: [number, number];
  float: [number, number];
  marketCap: [number, number];
  history: [number, number];
}

// --- Defaults ---

export const DEFAULTS: Filters = {
  price: [0, Number.MAX_SAFE_INTEGER],
  adv: [0, Number.MAX_SAFE_INTEGER],
  float: [0, 100],
  marketCap: [0, Number.MAX_SAFE_INTEGER],
  history: [0, Number.MAX_SAFE_INTEGER],
};

export function isActive(
  current: [number, number],
  dataRange: [number, number],
) {
  return current[0] > dataRange[0] || current[1] < dataRange[1];
}

// --- Formatters (axis labels) ---

// Compact USD, one shared ladder for every money axis and money input on this
// step. Keeping ADV and Market Cap on separate ladders is what produced
// "$42148.4M" on one axis and "$42.1B" on another for the same magnitude.
// Above a thousand the suffix carries the scale; below it, the raw number gets
// thousands separators — "$2495000" is not a price anyone can read at a glance.
export function fmtUsd(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000_000) return `$${(v / 1e12).toFixed(1)}T`;
  if (abs >= 1_000_000_000) return `$${(v / 1e9).toFixed(1)}B`;
  // One decimal only while it carries information: $7.2M is worth reading,
  // $130.0M is noise.
  if (abs >= 1_000_000) return `$${(v / 1e6).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString('en-US')}`;
}

export function fmtPrice(v: number) {
  // Prices stay unabbreviated — a share price is read exactly, not in
  // magnitudes — but grouped: $2,495,000 rather than $2495000.
  return `$${Math.round(v).toLocaleString('en-US')}`;
}

export function fmtAdv(v: number) {
  return fmtUsd(v);
}

export function fmtFloat(v: number) {
  return `${v.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
}

export function fmtMcap(v: number) {
  return fmtUsd(v);
}

export function fmtDays(v: number) {
  return `${Math.round(v).toLocaleString('en-US')}d`;
}

// --- Formatters (input display) ---

export function fmtPriceInput(v: number) {
  return `$${v.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function fmtAdvInput(v: number) {
  return fmtUsd(v);
}

export function fmtFloatInput(v: number) {
  return `${v.toFixed(2)}%`;
}

export function fmtMcapInput(v: number) {
  return fmtUsd(v);
}

export function fmtDaysInput(v: number) {
  if (v >= 60) return `${Math.round(v / 30).toLocaleString('en-US')} Months`;
  return `${Math.round(v).toLocaleString('en-US')} Days`;
}

// --- Parsers (input → number) ---

export function parsePrice(s: string): number | null {
  const n = parseFloat(s.replace(/[$,]/g, ''));
  return isNaN(n) ? null : n;
}

// The display writes K/M/B/T, so the parser reads them back — "$42.1B" parsed
// on the old millions-only rule became $42.1M, shrinking the bound by three
// orders of magnitude.
//
// A BARE number is raw dollars, NOT millions. The field seeds its edit box with
// the raw grouped value it holds (e.g. "6,100,000"), so the number the user
// types back must round-trip to that same value. Reading a bare number as
// millions was the "type any min and it jumps to the max" bug: "6,100,000"
// became 6.1e12 — above every ceiling — and clamped to the cap. Power users can
// still scale with an explicit K/M/B/T suffix.
function parseScaledUsd(s: string): number | null {
  const cleaned = s.trim().replace(/[$,\s]/g, '');
  const n = parseFloat(cleaned);
  if (isNaN(n)) return null;
  if (/t$/i.test(cleaned)) return n * 1e12;
  if (/b$/i.test(cleaned)) return n * 1e9;
  if (/m$/i.test(cleaned)) return n * 1e6;
  if (/k$/i.test(cleaned)) return n * 1e3;
  return n;
}

export function parseAdv(s: string): number | null {
  return parseScaledUsd(s);
}

export function parseFloat_(s: string): number | null {
  const n = parseFloat(s.replace(/%/g, ''));
  return isNaN(n) ? null : n;
}

export function parseMcap(s: string): number | null {
  return parseScaledUsd(s);
}

export function parseDays(s: string): number | null {
  const cleaned = s.trim().replace(/,/g, '');
  const n = parseFloat(cleaned.replace(/[^0-9.]/g, ''));
  if (isNaN(n)) return null;
  // Listing history is WHOLE days — always round to an integer. fmtDaysInput
  // switches to months past 60 days, so the parser has to switch back too:
  // without it a 2,500-day bound rendered as "83 Months" came back as 83 DAYS.
  return Math.round(/month/i.test(cleaned) ? n * 30 : n);
}
